"use client";

import React, { useEffect, useRef, useState } from "react";
import { Images, Star, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const TOTAL = 15;
const photos = ["https://charming-joey.10web.cloud/wp-content/uploads/2026/08/agatas-sumazintos_TomaJfoto-1-scaled.jpg", "https://charming-joey.10web.cloud/wp-content/uploads/2026/07/Agatas-iejimas-su-Irma-1.jpg", "https://charming-joey.10web.cloud/wp-content/uploads/2026/08/agatas-sumazintos_TomaJfoto_17-scaled.jpg", "https://charming-joey.10web.cloud/wp-content/uploads/2026/08/agatas-sumazintos_TomaJfoto_18-scaled.jpg", "https://charming-joey.10web.cloud/wp-content/uploads/2026/08/agatas-sumazintos_TomaJfoto_7-scaled.jpg", "https://charming-joey.10web.cloud/wp-content/uploads/2026/08/agatas-sumazintos_TomaJfoto_11-scaled.jpg", "https://charming-joey.10web.cloud/wp-content/uploads/2026/08/Sumaintos-agatas_TomaJfoto_81-copy-scaled.jpg", "https://charming-joey.10web.cloud/wp-content/uploads/2026/08/agatas-sumazintos_TomaJfoto_14-scaled.jpg", "https://charming-joey.10web.cloud/wp-content/uploads/2026/08/agatas-sumazintos_TomaJfoto_3-scaled.jpg", "https://charming-joey.10web.cloud/wp-content/uploads/2026/08/agatas-sumazintos_TomaJfoto_5-scaled.jpg", "https://charming-joey.10web.cloud/wp-content/uploads/2026/08/agatas-sumazintos_TomaJfoto_2-scaled.jpg", "https://charming-joey.10web.cloud/wp-content/uploads/2026/08/agatas-sumazintos_TomaJfoto_9-scaled.jpg", "https://charming-joey.10web.cloud/wp-content/uploads/2026/08/agatas-sumazintos_TomaJfoto_15-scaled.jpg", "https://charming-joey.10web.cloud/wp-content/uploads/2026/08/Sumazintos-agatas_TomaJfoto_61-copy--scaled.jpg", "https://charming-joey.10web.cloud/wp-content/uploads/2026/08/Sumaintos-agatas_TomaJfoto-scaled.jpg"];
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
function GalleryModal({
  onClose
}: {
  onClose: () => void;
}) {
  const [current, setCurrent] = useState(0);
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchEndX, setTouchEndX] = useState(0);
  const prev = () => setCurrent(i => i === 0 ? TOTAL - 1 : i - 1);
  const next = () => setCurrent(i => i === TOTAL - 1 ? 0 : i + 1);
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEndX(0);
    setTouchStartX(e.targetTouches[0].clientX);
  };
  const onTouchMove = (e: React.TouchEvent) => setTouchEndX(e.targetTouches[0].clientX);
  const onTouchEnd = () => {
    if (!touchStartX || !touchEndX) return;
    const distance = touchStartX - touchEndX;
    if (distance > 50) next();
    if (distance < -50) prev();
    setTouchStartX(0);
    setTouchEndX(0);
  };
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);
  return <div className="fixed inset-0 z-50 bg-white/98 flex items-center justify-center p-4" onClick={onClose}>
      <div className="relative w-full max-w-4xl max-h-[90vh]" onClick={e => e.stopPropagation()}>
        <Button variant="ghost" size="icon" onClick={onClose} className="absolute -top-10 right-0 h-8 w-8 text-[#264256] hover:bg-transparent hover:text-[#65d19d] transition-colors z-10 p-0">
          <X className="w-8 h-8" />
        </Button>
        <div className="relative min-h-[300px] flex items-center justify-center" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
          {photos.map((src, i) => <img key={i} data-index={i} src={src} alt={`Clinic Agatas photo ${i + 1}`} className={`w-full max-h-[75vh] object-contain rounded-lg shadow-2xl ${current === i ? "block" : "hidden"}`} />)}
          <Button variant="ghost" size="icon" onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-lg bg-[#264256]/70 hidden md:flex items-center justify-center text-white hover:bg-[#264256] hover:text-white transition-colors">
            <ChevronLeft className="w-6 h-6" />
          </Button>
          <Button variant="ghost" size="icon" onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-lg bg-[#264256]/70 hidden md:flex items-center justify-center text-white hover:bg-[#264256] hover:text-white transition-colors">
            <ChevronRight className="w-6 h-6" />
          </Button>
        </div>
        <div className="hidden md:flex justify-center gap-2 mt-4">
          {photos.map((_, i) => <Button key={i} data-index={i} variant="ghost" onClick={() => setCurrent(i)} className={`h-2 w-2 min-w-0 p-0 rounded-lg transition-colors ${i === current ? "bg-[#264256] hover:bg-[#264256]" : "bg-[#264256]/40 hover:bg-[#264256]/60"}`} />)}
        </div>
        <p className="md:hidden mt-4 flex items-center justify-center gap-2 text-[#264256]/80 text-sm tracking-widest uppercase">
          <ChevronLeft className="w-4 h-4" /> Swipe <ChevronRight className="w-4 h-4" />
        </p>
        <p className="text-center text-[#264256]/70 text-sm mt-2">{current + 1} / {TOTAL}</p>
      </div>
    </div>;
}
export default function YourPartnerInLifelongHealth() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const [galleryOpen, setGalleryOpen] = useState(false);
  return <section data-section-id="175584" id="your-path-to-pain-relief" className="relative w-full overflow-hidden bg-[#264256] pt-10 pb-16 lg:pt-14 lg:pb-24">
      {galleryOpen && <GalleryModal onClose={() => setGalleryOpen(false)} />}

      <style dangerouslySetInnerHTML={{
      __html: `
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob { animation: blob 10s infinite alternate; }
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }
      `
    }} />

      <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
        <div className="animate-blob absolute top-0 left-1/4 h-[500px] w-[500px] rounded-lg bg-accent/20 mix-blend-multiply blur-3xl filter"></div>
        <div className="animate-blob animation-delay-2000 absolute top-0 right-1/4 h-[400px] w-[400px] rounded-lg bg-white/10 mix-blend-multiply blur-3xl filter"></div>
        <div className="animate-blob animation-delay-4000 absolute -bottom-32 left-1/3 h-[600px] w-[600px] rounded-lg bg-accent/10 mix-blend-multiply blur-3xl filter"></div>
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6 lg:px-8">
        <div className="grid items-center gap-8 lg:grid-cols-12 lg:gap-6">

          <div ref={textRef} className={`lg:col-span-7 transition-all duration-1000 ease-out ${isTextVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
            <h1 className="font-serif font-light leading-[1.1] tracking-tight text-white !text-[38px] lg:!text-[46px]">
              You've waited long enough.<br />Treatment&nbsp;<span className="text-[#a9e5c9]">in weeks</span>.
            </h1>

            <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/90 md:text-lg font-default">Almost 1 million people are now on HSE waiting lists (NTPF, August 2026), with 687,839 waiting just for an outpatient appointment. Irish patients can access specialist pain management and spine surgery at Clinic Agatas in Lithuania, with treatment costs reimbursed by the HSE through the Cross-Border Directive. See exactly what you can claim.</p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
             <Button size="lg" className="h-12 rounded-lg px-7 text-base shadow-lg shadow-black/30 bg-[#65d19d] text-[#264256] border border-[#65d19d] hover:bg-[#65d19d]/90" asChild>
                <Link to="#request-your-consultation">Book a consultation</Link>
              </Button>
              <Button size="lg" variant="outline" className="h-12 rounded-lg border-white/30 bg-white/10 px-7 text-base text-white backdrop-blur-sm hover:bg-white/20" onClick={() => setGalleryOpen(true)}>
                <Images className="mr-2 size-4" aria-hidden="true" />
                See the clinic
              </Button>
            </div>
          </div>

          <div className="relative mt-8 lg:col-span-5 lg:mt-0">
            <div className="relative aspect-[4/3.2] w-full overflow-hidden rounded-tl-[84px] rounded-br-[84px] rounded-tr-[21px] rounded-bl-[21px] border border-white/20 bg-primary/40 shadow-2xl md:aspect-[4/3.2] lg:aspect-[4/3.2]">
              <img src="https://charming-joey.10web.cloud/wp-content/uploads/2026/05/Jusu-paragrafo-tekstas-9.png" alt="Experienced spine surgeon at Clinic Agatas" className="h-full w-full object-cover object-bottom" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/60 via-transparent to-transparent"></div>
            </div>

            <Card className="absolute -bottom-[55px] -left-[111px] w-[240px] animate-in fade-in slide-in-from-bottom-8 border-white/20 bg-white/95 shadow-xl backdrop-blur-md duration-1000 delay-500 fill-mode-both md:-left-[143px] lg:-left-[159px]">
              <CardContent className="flex items-center gap-4 p-3">
                <div className="flex -space-x-3">
                  <div className="flex size-9 items-center justify-center overflow-hidden rounded-lg border-2 border-white bg-muted">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1626927/img/generic_avatar.png" alt="Patient" className="h-full w-full object-cover" loading="lazy" />
                  </div>
                  <div className="flex size-9 items-center justify-center overflow-hidden rounded-lg border-2 border-white bg-muted">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1626927/img/generic_avatar.png" alt="Patient" className="h-full w-full object-cover" loading="lazy" />
                  </div>
                  <div className="flex size-9 items-center justify-center rounded-lg border-2 border-white bg-accent text-xs font-bold text-accent-foreground">65</div>
                </div>
                <div className="flex flex-col">
                  <div className="flex items-center gap-1">
                    <Star className="size-4 fill-accent text-accent" />
                    <span className="font-default font-bold text-primary">4.6/5</span>
                  </div>
                  <span className="font-default text-xs font-medium text-primary/60">Google Rating</span>
                </div>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}